import React, { useMemo } from "react";
import "./InsideValuation.css";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import LayersIcon from "@mui/icons-material/Layers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpIcon from "@mui/icons-material/Help";

/**
 * Inside the Valuation – Emerald Appraisal Services
 * Client-aligned updates:
 *  - "defensible" wording removed per request (now "well-supported conclusion")
 *  - Section theme shifted to emerald (more green)
 * Props:
 *  - items?: custom array of { title, desc, icon, wide? }
 */
export default function InsideValuation({ items }) {
  const data = useMemo(
    () =>
      items ?? [
        {
          title: "Recent, Relevant Sales",
          desc: "True comparability based on condition, size, utility, location, and proximity to the subject.",
          icon: "sales",
        },
        {
          title: "Market Momentum",
          desc: "Days on market, list-to-sale ratios, inventory shifts, and seasonal patterns that influence value.",
          icon: "trend",
        },
        {
          title: "Property Characteristics",
          desc: "Layout and livability, construction quality, renovations, site/lot factors, and view impacts.",
          icon: "home",
        },
        {
          title: "External Influences",
          desc: "School districts, access and commute, employment centers, neighborhood context, and zoning.",
          icon: "map",
        },
        {
          title: "Cost & Income Cross-Checks",
          desc: "When relevant, cost and income perspectives to test and support the sales comparison approach.",
          icon: "layers",
        },
        {
          title: "Adjustment Rationale",
          desc: "Adjustments are supported by data, paired-sales logic, and documented reasoning for a well-supported conclusion.",
          icon: "check",
        },
      ],
    [items]
  );

  return (
    <section className="InsideValuation" aria-labelledby="iv-title">
      <div className="InsideValuation__container">
        {/* Header */}
        <header className="InsideValuation__header">
          <p className="InsideValuation__eyebrow">Inside the Valuation</p>
          <h2 id="iv-title" className="InsideValuation__title">
            What we actually analyze
          </h2>
          <p className="InsideValuation__lead">
            A reliable opinion of value is built from converging evidence—never a single number.
            Here's how we examine the data that matters.
          </p>
        </header>

        {/* Grid */}
        <ul className="InsideValuation__grid">
          {data.map((item, i) => (
            <li
              key={i}
              className={`InsideValuation__card ${item.wide ? "InsideValuation__card--wide" : ""}`}
            >
              <span
                className={`InsideValuation__icon InsideValuation__icon--${item.icon}`}
                aria-hidden="true"
              >
                {getIcon(item.icon)}
              </span>
              <div className="InsideValuation__content">
                <h3 className="InsideValuation__point">{item.title}</h3>
                <p className="InsideValuation__desc">{item.desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* Material Icons */
function getIcon(name) {
  switch (name) {
    case "sales":
      return <AnalyticsIcon fontSize="medium" />;
    case "trend":
      return <TrendingUpIcon fontSize="medium" />;
    case "home":
      return <HomeIcon fontSize="medium" />;
    case "map":
      return <MapIcon fontSize="medium" />;
    case "layers":
      return <LayersIcon fontSize="medium" />;
    case "check":
      return <CheckCircleIcon fontSize="medium" />;
    default:
      return <HelpIcon fontSize="medium" />;
  }
}
